/**
 * Provides a base component for animated indicators.
 */
import * as React from 'react';

import { Animated, Easing, EasingFunction, Platform, ViewProps, ViewStyle } from 'react-native';

export class Indicator extends React.PureComponent<IndicatorProps, IndicatorState> {
  public static defaultProps = {
    animating: true,
    animationDuration: 1200,
    animationEasing: Easing.linear,
    count: 1,
    interaction: true,
  };

  private mounted: boolean;

  public constructor(props: IndicatorProps) {
    super(props);

    this.renderComponent = this.renderComponent.bind(this);
    this.startAnimation = this.startAnimation.bind(this);
    this.stopAnimation = this.stopAnimation.bind(this);

    this.state = {
      progress: new Animated.Value(0),
    };

    this.mounted = false;
  }

  public componentDidMount() {
    const { animating } = this.props;

    this.mounted = true;

    if (animating) {
      this.startAnimation();
    }
  }

  public UNSAFE_componentWillReceiveProps(props: IndicatorProps) {
    const { animating } = this.props;

    if (animating !== props.animating) {
      if (animating) {
        this.stopAnimation();
      } else {
        this.startAnimation();
      }

      this.startAnimation();
    }
  }

  public componentWillUnmount() {
    this.mounted = false;
  }

  public render() {
    const { count, ...props } = this.props;

    return (
      <Animated.View {...props}>
        {Array.from({ length: count }, this.renderComponent)}
      </Animated.View>
    );
  }

  private renderComponent(_ignored: unknown, index: number) {
    const { progress } = this.state;
    const { renderComponent, count } = this.props;

    if (typeof renderComponent === 'function') {
      return renderComponent({ index, count, progress });
    }

    return null;
  }

  private startAnimation({ finished } = { finished: undefined }) {
    const { progress } = this.state;
    const { animationEasing, animationDuration } = this.props;

    if (!this.mounted || finished === false) {
      return;
    }

    const animation = Animated.timing(progress, {
      duration: animationDuration,
      easing: animationEasing,
      // isInteraction: interaction,
      isInteraction: false,
      toValue: 1,
      useNativeDriver: Platform.OS !== 'web',
    });

    Animated.loop(animation).start();

    this.setState({ animation });
  }

  private stopAnimation() {
    const { animation } = this.state;

    if (!animation) {
      return;
    }

    animation.stop();

    this.setState({ animation: undefined });
  }
}

/* Type definitions */
export interface IndicatorRenderOptions {
  count: number;
  index: number;
  progress: Animated.Value;
}
export interface IndicatorProps extends ViewProps {
  animating?: boolean;
  animationDuration?: number;
  animationEasing?: EasingFunction;
  count: number;
  interaction?: boolean;
  style?: ViewStyle;
  renderComponent?(options: IndicatorRenderOptions): React.ReactElement;
}

interface IndicatorState {
  animation?: Animated.CompositeAnimation;
  progress: Animated.Value;
}
